import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bell, MessageSquare, Filter, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function SollecitiPage() {
  const [solleciti, setSolleciti] = useState([]);
  const [insolute, setInsolute] = useState([]);
  const [sending, setSending] = useState("");
  const [fClasse, setFClasse] = useState("tutti");
  const [fStato, setFStato] = useState("tutti");
  const [fDal, setFDal] = useState("");
  const [fAl, setFAl] = useState("");

  const loadSolleciti = useCallback(() => api.get("/solleciti").then(r => setSolleciti(r.data)).catch(() => {}), []);
  const loadInsolute = useCallback(() => api.get("/rate-insolute").then(r => setInsolute(r.data)).catch(() => {}), []);
  useEffect(() => {
    loadSolleciti();
    loadInsolute();
  }, [loadSolleciti, loadInsolute]);

  const inviaSollecito = async (item) => {
    if (sending) return;
    setSending(item.rata_id);
    try {
      await api.post(`/piani-rate/${item.piano_id}/sollecita/${item.rata_id}`);
      toast.success(`Sollecito inviato a ${item.studente_nome} via email`);
      loadInsolute();
      loadSolleciti();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore invio sollecito"); }
    finally { setSending(""); }
  };

  const classi = [...new Set(solleciti.map(s => s.classe_nome).filter(n => n && n !== "-"))];
  const filtrati = solleciti.filter(s => {
    if (fClasse !== "tutti" && s.classe_nome !== fClasse) return false;
    if (fStato === "aperto" && s.rata_ora_pagata) return false;
    if (fStato === "regolarizzato" && !s.rata_ora_pagata) return false;
    const d = s.data?.slice(0,10);
    if (fDal && d < fDal) return false;
    if (fAl && d > fAl) return false;
    return true;
  });

  const aperti = filtrati.filter(s => !s.rata_ora_pagata);
  const regolarizzati = filtrati.filter(s => s.rata_ora_pagata);

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#F7941E] mb-2">Recupero Crediti</div>
        <h1 className="text-3xl font-bold text-stone-900">Dashboard Solleciti</h1>
        <p className="text-stone-600 mt-1">Solleciti manuali via email · Storico invii e stato regolarizzazione</p>
      </div>

      <Card className="p-4 mb-6 bg-white border-[#E2E8F0]">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-stone-900">Rate insolute da sollecitare</div>
          <div className="text-sm text-stone-500" data-testid="insolute-count">{insolute.length} rate scadute non pagate</div>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Studente</TableHead><TableHead>Corso</TableHead><TableHead>Rata</TableHead>
            <TableHead>Scadenza</TableHead><TableHead>Ultimo sollecito</TableHead><TableHead className="text-right">Azione</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {insolute.map(i => (
              <TableRow key={`${i.piano_id}-${i.rata_id}`} data-testid={`insoluta-row-${i.rata_id}`}>
                <TableCell className="font-medium">{i.studente_nome}<div className="text-xs text-stone-500">{i.studente_email}</div></TableCell>
                <TableCell className="text-sm">{i.corso_titolo}</TableCell>
                <TableCell className="font-mono text-sm">n.{i.numero} · € {i.importo?.toFixed(2)}</TableCell>
                <TableCell className="text-sm text-red-600 font-medium">{i.scadenza}</TableCell>
                <TableCell className="text-sm text-stone-500">{i.ultimo_sollecito ? i.ultimo_sollecito.slice(0, 16).replace("T", " ") : "Mai"}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" onClick={() => inviaSollecito(i)} disabled={sending === i.rata_id}
                    className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid={`invia-sollecito-${i.rata_id}`}>
                    <Send className="w-4 h-4 mr-1"/>{sending === i.rata_id ? "Invio..." : "Invia sollecito"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {insolute.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-stone-500 py-6">Nessuna rata insoluta: tutti i pagamenti sono regolari.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-4 mb-6 bg-white border-[#E2E8F0]">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 text-sm text-stone-600"><Filter className="w-4 h-4 text-[#F7941E]"/> Filtri storico:</div>
          <div className="min-w-[200px]">
            <div className="text-xs text-stone-500 mb-1">Classe</div>
            <Select value={fClasse} onValueChange={setFClasse}>
              <SelectTrigger data-testid="filtro-classe"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutte le classi</SelectItem>
                {classi.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <div className="text-xs text-stone-500 mb-1">Stato</div>
            <Select value={fStato} onValueChange={setFStato}>
              <SelectTrigger data-testid="filtro-stato"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti</SelectItem>
                <SelectItem value="aperto">Aperti</SelectItem>
                <SelectItem value="regolarizzato">Regolarizzati</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs text-stone-500 mb-1">Dal</div>
            <Input type="date" value={fDal} onChange={e=>setFDal(e.target.value)} data-testid="filtro-dal" className="w-40"/>
          </div>
          <div>
            <div className="text-xs text-stone-500 mb-1">Al</div>
            <Input type="date" value={fAl} onChange={e=>setFAl(e.target.value)} data-testid="filtro-al" className="w-40"/>
          </div>
          {(fClasse!=="tutti" || fStato!=="tutti" || fDal || fAl) && (
            <button onClick={()=>{setFClasse("tutti");setFStato("tutti");setFDal("");setFAl("");}} className="text-sm text-[#F7941E] hover:underline" data-testid="filtro-reset">Azzera filtri</button>
          )}
        </div>
      </Card>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5 bg-white border-[#E2E8F0]" data-testid="solleciti-kpi-totali">
          <div className="flex items-center gap-3">
            <Bell className="w-8 h-8 text-[#F7941E]"/>
            <div>
              <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Solleciti Totali</div>
              <div className="text-3xl font-bold text-stone-900">{filtrati.length}</div>
            </div>
          </div>
        </Card>
        <Card className="p-5 bg-white border-[#E2E8F0]" data-testid="solleciti-kpi-aperti">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-red-600"/>
            <div>
              <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Ancora Aperti</div>
              <div className="text-3xl font-bold text-red-600">{aperti.length}</div>
            </div>
          </div>
        </Card>
        <Card className="p-5 bg-white border-[#E2E8F0]" data-testid="solleciti-kpi-regolarizzati">
          <div className="flex items-center gap-3">
            <Bell className="w-8 h-8 text-emerald-600"/>
            <div>
              <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Regolarizzati</div>
              <div className="text-3xl font-bold text-emerald-600">{regolarizzati.length}</div>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-4 bg-white border-[#E2E8F0]">
        <div className="font-semibold text-stone-900 mb-3">Storico solleciti inviati</div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Data invio</TableHead><TableHead>Studente</TableHead><TableHead>Classe</TableHead>
            <TableHead>Rata</TableHead><TableHead>Scadenza</TableHead><TableHead>Stato</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtrati.map(s => (
              <TableRow key={s.id} data-testid={`sollecito-row-${s.id}`}>
                <TableCell className="text-sm">{s.data?.slice(0, 16).replace("T", " ")}</TableCell>
                <TableCell className="font-medium">{s.studente_nome}<div className="text-xs text-stone-500">{s.studente_email}</div></TableCell>
                <TableCell className="text-sm">{s.classe_nome}</TableCell>
                <TableCell className="font-mono text-sm">n.{s.rata?.numero} · € {s.rata?.importo?.toFixed(2)}</TableCell>
                <TableCell className="text-sm">{s.rata?.scadenza}</TableCell>
                <TableCell>
                  {s.rata_ora_pagata
                    ? <Badge className="bg-emerald-50 text-emerald-800">Regolarizzato</Badge>
                    : <Badge className="bg-red-50 text-red-800">Aperto</Badge>}
                </TableCell>
              </TableRow>
            ))}
            {filtrati.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-stone-500">Nessun sollecito per i filtri selezionati.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
